/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
import { MARGIN_WIDTH_PX, NewStyledEmptyNode, NodeComponent, StyledEmptyNodeMessage, useTreeBrowser } from "@iventis/tree-browser";
import React, { FunctionComponent, ReactNode, useEffect, useState, useMemo, useRef, MouseEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { findNode, findNodesByType, flattenNodeTree, getNodeNestedLevel, isOnlyChildInSelection } from "@iventis/tree-browser/tree-operations";
import { useDispatch, useSelector } from "react-redux";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Node } from "@iventis/domain-model/model/node";
import { LoadingComponent, DisabledOverlay, InfoTooltip, ClickAwayListenerToolTip, ConfirmationDialogComponent } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import { InteractiveElement } from "@iventis/styles/src/components";
import { LayerStyle } from "@iventis/domain-model/model/layerStyle";
import { ClickAwayListener, useTheme } from "@mui/material";
import { getLayerStyle } from "@iventis/layer-style-helpers/src/get-layer-style-helpers";
import { permissableAuthorisations, isAdmin, canEdit, sanitiseForDOMToken } from "@iventis/utilities";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { LayerEditModal } from "@iventis/layer-styles/src/edit-layer-modal";
import { DragAndDropItemPlacement, useDragAndDropSortable } from "@iventis/drag-and-drop";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { skeletonShimmer, fontSizes, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { dataTestIds } from "@iventis/testing";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { ViewByStatus } from "@iventis/map-engine/src/types/layer-view-by";
import { updateUserPreferences } from "@iventis/permissions/src/auth.slice";
import { SidebarItemComponent } from "@iventis/components/src/sidebar-item";
import { assetUrlGetter } from "@iventis/api/src/api";
import { LayerIconGenerated } from "@iventis/map-object-thumbnails";
import { useQuery } from "@tanstack/react-query";
import { PermissionsEditModalComponent } from "@iventis/permissions/src/permissions-edit-modal";
import { toast } from "@iventis/toasts/src/toast";
import { useHistory } from "@iventis/subscriptions";
import { useUpgradePlanAlert } from "@iventis/permissions/src/upgrade-subscription-alert";
import { ExportType, createMapObjectExportNotification, notificationNameCreator, useNotificationContext } from "@iventis/notifications";
import { getNextHighestLayerMapOrderValue, getNextLowestLayerMapOrderValue } from "@iventis/map-engine/src/bridge/mapbox/layer-ordering/layer-ordering-generic-helpers";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { LocalGeoJsonObject, MapObjectProperties } from "@iventis/map-types";
import { getMapObjectNameSystemDatafield, updatedQuantityDataFieldValueIfNull } from "@iventis/datafields";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries/src/error-boundary-with-toast";
import { assetService } from "../services/asset-service";
import { SelectedNodeIndicator, StyledSidebarNode } from "./sidebar-node.styles";
import { MapInterfaceStyle } from "../state/sidebar.slice.types";
import { sidebar } from "../state/sidebar.slice";
import { RootDispatch, RootState, rootStore } from "../state/root.store";
import { map } from "../state/map.slice";
import { SidebarTreeBrowserExtraProps } from "../sidebar";
import { MapLayerPatchType, patchMapLayers, patchMapObjectsDataFieldValues } from "../state/map.slice.thunks";
import { getMapLayerNodeFromOriginId, getParentLayer, getParentNode, ToggleableNodeAttributes, getMapObjectLevelName } from "./sidebar-node.helpers";
import { LockIconComponent } from "./sidebar-node-locking";
import { LayerInformation } from "./sidebar-node-information";
import { EyeIconComponent } from "./sidebar-node-visbility";
import { SidebarContextMenu } from "../sidebar-context-menu";
import { DeleteNodeModal } from "./sidebar-modals";
import { ModalState } from "../sidebar-types";
import { exportLayerAsGeoJson, exportLayerAsKml } from "../sidebar-export-helpers";
import { FROM_MAP_ROOT } from "../map-constants";
import { checkIfMapObjectHasWhat3WordsValue } from "../map-container-what3words";
import { handleSelectedMapObject } from "../map-object-selection-helpers";
import { getLayerThumbnailAssets } from "../../src/state/map.slice.functions";
import { editStyleService } from "../services/edit-style-service";
import { removeLayerObjectsFromStack, useMapUndoRedoContext } from "../map-undo-redo-context";
import { patchMapGroup } from "../services/map-groups-api-functions";

const SidebarTreeNodeComponent: FunctionComponent<{
    children: ReactNode;
    node: Node;
    mapLayer?: DomainLayer;
    indent: number;
    expanded?: boolean;
    canExpand?: boolean;
    setSidebarPopoverAnchor: (anchor: Element) => void;
    handleDeleteNodes: (nodes: Node[]) => void;
    isLoading?: boolean;
    isDragging?: boolean;
    setLayerActionModal?: (modalState: ModalState) => void;
    additionalElements?: ReactNode;
    isInGroup?: boolean;
}> = ({
    children,
    node,
    mapLayer,
    indent,
    expanded,
    canExpand,
    isLoading = false,
    isDragging = false,
    handleDeleteNodes,
    setLayerActionModal,
    additionalElements,
    isInGroup = false,
}) => {
    const {
        mapInterfaceStyle,
        projectDataFields,
        popoverNodeId,
        mapLayers,
        mode,
        modalLayerId,
        mapObjects,
        unitOfMeasurement,
        userId,
        projectId,
        mapName,
        level,
        sitemaps,
        datesFilter,
    } = useSelector((s: RootState) => ({
        mapObjectsSelected: s.mapReducer.mapModule.mapObjectsSelected.value,
        mapInterfaceStyle: s.sidebarReducer.mapInterfaceStyle,
        projectDataFields: s.mapReducer.mapModule.projectDataFields,
        popoverNodeId: s.sidebarReducer.popover.nodeId,
        mapLayers: s.mapReducer.mapModule.layers.value,
        mode: s.mapReducer.mapModule.mode,
        modalLayerId: s.sidebarReducer.modalLayer?.id,
        mapObjects: s.mapReducer.mapModule.geoJSON,
        unitOfMeasurement: s.mapReducer.mapModule.unitOfMeasurement.value,
        userId: s.auth.user.userId,
        projectId: s.auth.user.projectId,
        mapName: s.mapReducer.mapName,
        level: s.mapReducer.mapModule.currentLevel,
        sitemaps: s.mapReducer.sitemaps,
        datesFilter: s.mapReducer.mapModule.datesFilter.value,
    }));
    const { createNotification } = useNotificationContext();
    const upgradePlan = useUpgradePlanAlert();

    const [onMouseOver, setMouseOver] = useState(false);

    const [layerInfo, setLayerInfo] = useState<boolean>(false);
    const [{ selectedNodeIds, tree, mainNodeId }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "selectedNodeIds", "tree", "mainNodeId");

    // Any map-specific properties that can be updated, we must check map authorisation (e.g. visibility and locked)
    const mapAuthorisation = useMemo(() => findNode([tree], mainNodeId)?.authorisation, [mainNodeId]);

    const dispatch: RootDispatch = useDispatch();

    const nodeHeight = mapInterfaceStyle === MapInterfaceStyle.COMPRESSED ? 34 : 44;

    const [editName, setEditName] = useState(false);

    const [permissionModalOpen, setPermissionModalOpen] = useState(false);

    const translate = useIventisTranslate();

    const handleExportLayers = (exportType: "geojson" | "kml" | "dwg") => {
        switch (exportType) {
            case "geojson":
                {
                    const selectedLayers = mapLayers.filter((layer) => selectedNodeIds.includes(layer.id));
                    selectedLayers.forEach(({ id }) => {
                        incrementMonitoringCount({ name: MetricName.MAP_OBJECTS_EXPORTED_GEOJSON, resourceId: id });
                    });
                    const request = exportLayerAsGeoJson(selectedLayers, selectedLayers[0].mapId, unitOfMeasurement);
                    const notification = createMapObjectExportNotification(userId, projectId, notificationNameCreator(ExportType.LayerGeoJson, mapName), request);
                    createNotification(notification);
                }
                break;
            case "kml":
                {
                    const selectedLayers = mapLayers.filter((layer) => selectedNodeIds.includes(layer.id));
                    selectedLayers.forEach(({ id }) => {
                        incrementMonitoringCount({ name: MetricName.MAP_OBJECTS_EXPORTED_KML, resourceId: id });
                    });
                    const request = exportLayerAsKml(selectedLayers, selectedLayers[0].mapId, unitOfMeasurement);
                    const notification = createMapObjectExportNotification(userId, projectId, notificationNameCreator(ExportType.LayerKml, mapName), request);
                    createNotification(notification);
                }
                break;
            case "dwg":
                setLayerActionModal("exportDwg");
                break;
            default:
                throw new Error(`Export type ${exportType} not handled`);
        }
    };

    const selectNode = async (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        // If selection is map object
        if (node.type === NodeType.MapObject) {
            const layer = mapLayers.find((l) => l.id === node.parentId);

            const mapObject = mapObjects.value[layer.id].find((mo) => mo.objectId === node.id);

            if (mapObject == null) {
                throw Error("Map object not found");
            }

            const levels = getAllSitemapLevels(sitemaps);
            const currentMapObjectLevel = node.type === NodeType.MapObject && levels.length > 1 ? mapObject?.feature?.properties.level : 0;
            // pan to map object and show toast if layer is hidden or on a different level
            handleSelectedMapObject({ ...layer, authorisation: node.authorisation }, mapObject?.feature, currentMapObjectLevel, level, dispatch, map, translate, datesFilter, () =>
                treeBrowserContext.toggleSelectNodes([node], { from: FROM_MAP_ROOT })
            );

            // Check if line model layer has a quantity datafield and update it if null
            if (layer.styleType === StyleType.LineModel) {
                const objectsWithUpdatedQuantity = updatedQuantityDataFieldValueIfNull(
                    [{ layerId: layer.id, objectId: mapObject.objectId }],
                    [layer],
                    mapObjects.value,
                    projectDataFields
                );
                if (objectsWithUpdatedQuantity.length > 0) {
                    dispatch(map.actions.patchMapObjectDataFieldValues(objectsWithUpdatedQuantity));
                }
            }

            // And the layer has a what3words datafield
            if (layer.dataFields.some((df) => df.type === DataFieldType.What3Words)) {
                // Get what3words value for the map object
                checkIfMapObjectHasWhat3WordsValue([{ objectId: mapObject.objectId, feature: mapObject.feature, layerId: layer.id, waypoints: undefined }], [layer], dispatch);
            }
        } else {
            treeBrowserContext.toggleSelectNodes([node], { from: FROM_MAP_ROOT });
        }
    };

    const isSelected = selectedNodeIds.includes(node.id);

    const isEditingNode = useMemo(() => {
        const editMode = checkModeIsPresent(mode.value, "edit");
        const compMode = checkModeIsPresent(mode.value, "composition");
        switch (node.type) {
            case NodeType.Layer:
                return (compMode && selectedNodeIds.includes(node.id)) || (editMode && node.childNodes.some(({ id }) => selectedNodeIds.includes(id)));
            case NodeType.Folder:
                return (
                    (compMode && node.childNodes.some(({ id }) => selectedNodeIds.includes(id))) ||
                    (editMode && findNodesByType(node.childNodes, NodeType.MapObject)?.some(({ id }) => selectedNodeIds.includes(id)))
                );
            default:
                return false;
        }
    }, [mode.value, selectedNodeIds, node]);

    const [overflowOpen, setOverflowOpen] = useState(false);

    useEffect(() => {
        if (modalLayerId != null) setOverflowOpen(false);
    }, [modalLayerId]);

    // If a layer is no longer selected then we want to set the overflow to false
    useEffect(() => {
        if (!selectedNodeIds.includes(node.id)) {
            setOverflowOpen(false);
        }
    }, [selectedNodeIds]);

    const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
    const [toggleVisibilityConfirmationOpen, setToggleVisibilityConfirmationOpen] = useState(false);
    const overflowButtonRef = useRef<HTMLButtonElement>(null);

    const undoRedo = useMapUndoRedoContext();

    const checkChildrensToggleableAttribute = (attribute: ToggleableNodeAttributes, arrayMethod: (children: Node[]) => Array<Node>["some" | "every"]) => {
        if (node?.type === NodeType.Folder && node?.childNodes?.length > 0) {
            const children = flattenNodeTree(node).filter((n) => n.type === NodeType.Layer);
            return arrayMethod(children)((child) => mapLayers.find(({ id }) => id === child.id)?.[attribute]);
        }
        return false;
    };

    // When a folder is empty it can't be locked or visibility can't be toggled off

    const isEmptyFolderNode = node.type === NodeType.Folder && node.childNodes.length === 0;

    const isAllChildrenEmptyFolders = node.type === NodeType.Folder && flattenNodeTree(node).every((n) => n.type === NodeType.Folder);

    const groupIs: { [key in ToggleableNodeAttributes]?: boolean } = {
        visible: useMemo(() => {
            if (isEmptyFolderNode || isAllChildrenEmptyFolders) {
                return true;
            }
            return checkChildrensToggleableAttribute("visible", (layers) => layers.some.bind(layers));
        }, [mapLayer?.visible, isEmptyFolderNode, node]),
        locked: useMemo(() => {
            if (isEmptyFolderNode || isAllChildrenEmptyFolders) {
                return false;
            }
            return checkChildrensToggleableAttribute("locked", (layers) => layers.every.bind(layers));
        }, [mapLayer?.locked, isEmptyFolderNode, node]),
    };

    /**
     * Checks if the attribute on the node is enabled. Checks child nodes for folders.
     * @param attribute the attribute to check
     * @returns true if the attribute is enabled, false otherwise
     */
    const isNode: (attribute: ToggleableNodeAttributes) => boolean = (attribute) => {
        switch (node.type) {
            case NodeType.Layer:
            case NodeType.MapObject:
                return mapLayer?.[attribute];
            case NodeType.Folder:
                return groupIs[attribute];
            default:
                return true;
        }
    };

    const toggleAttribute: <TAttribute extends ToggleableNodeAttributes>(e: MouseEvent<HTMLElement>, attribute: TAttribute) => void = (e, attribute) => {
        e.stopPropagation();
        const undoRedoStackAffected = (attribute === "locked" && !isNode(attribute)) || (attribute === "visible" && isNode(attribute));

        if (node.type === NodeType.Folder) {
            const isUserAdminOnMap = isAdmin(mapAuthorisation);
            const newAttributeValue = !groupIs[attribute];
            const layerNodes = flattenNodeTree(node).filter((n) => n.type === NodeType.Layer);
            if (!isUserAdminOnMap && attribute === "visible") {
                const layerIdsWithVisibilityChange = layerNodes
                    .filter((layerNode) => {
                        const layer = mapLayers.find(({ id }) => id === layerNode.id);
                        return layer && layer.visible !== newAttributeValue;
                    })
                    .map((layer) => layer.id);

                dispatch(map.actions.updateLayersWithTemporaryVisibilityValue(layerIdsWithVisibilityChange));
            }
            const layers = layerNodes.map((child) => ({ ...mapLayers.find(({ id }) => id === child.id), [attribute]: newAttributeValue }));

            // If we lock any layers, ensure all their map object changes are removed from the undo/redo stack
            if (undoRedoStackAffected) {
                undoRedo.removeFromStack((acc, entry) =>
                    removeLayerObjectsFromStack(
                        acc,
                        entry,
                        layers.map((l) => l.id)
                    )
                );
            }

            dispatch(map.actions.updateMapLayers(layers));
            if (isUserAdminOnMap) {
                const layersPatch = layers.map<MapLayerPatchType>(({ id, mapId, ...layer }) => ({ id, mapId, [attribute]: layer[attribute] }));
                rootStore.dispatch(patchMapLayers(layersPatch));
            }
        }

        if (node.type === NodeType.Layer) {
            // If we lock a layer, ensure all their map object changes are removed from the undo/redo stack
            if (undoRedoStackAffected) {
                undoRedo.removeFromStack((acc, entry) => removeLayerObjectsFromStack(acc, entry, [node.id]));
            }
            if (isEditingNode) return;

            if (isAdmin(mapAuthorisation)) {
                rootStore.dispatch(patchMapLayers([{ id: node.id, [attribute]: !mapLayer[attribute] } as MapLayerPatchType]));
            } else {
                dispatch(map.actions.updateMapLayer({ ...mapLayer, [attribute]: !mapLayer[attribute] }));

                if (attribute === "visible") {
                    dispatch(map.actions.updateLayersWithTemporaryVisibilityValue([mapLayer.id]));
                }
            }
        }
    };

    // Show the disabled overlay when the user doesnt have permission for the resource.
    const disableOverlay = () =>
        !permissableAuthorisations.includes(node.authorisation) || mapLayer?.disabled ? <DisabledOverlay testId={`sidebar-node-${node.name}-disabled`} /> : null;

    const handleToggleAllLayersVisibility = () => {
        const userLayers = mapLayers.filter((l) => l.remote);
        const allUserLayersVisible = userLayers.every((l) => l.visible);
        const newVisible = !allUserLayersVisible;
        if (isAdmin(mapAuthorisation)) {
            rootStore.dispatch(patchMapLayers(userLayers.map((layer) => ({ id: layer.id, visible: newVisible }))));
        } else {
            dispatch(map.actions.updateMapLayers(userLayers.map((layer) => ({ ...layer, visible: newVisible }))));
        }
    };

    const handleNameChange = (_: never, name: string) => {
        const updatedNode = { ...node, name };
        switch (node.type) {
            case NodeType.Layer:
                rootStore.dispatch(patchMapLayers([{ id: updatedNode.id, name: updatedNode.name }] as MapLayerPatchType[]));
                treeBrowserContext.updateNodeLocally(updatedNode);
                break;
            case NodeType.Folder:
                patchMapGroup({ id: node.id, name }, mainNodeId);
                treeBrowserContext.updateNodeLocally(updatedNode);
                break;
            case NodeType.MapObject: {
                const mapObjectNameDataField = getMapObjectNameSystemDatafield(projectDataFields);
                const layerId = getMapLayerNodeFromOriginId(node.parentId, treeBrowserContext);
                dispatch(
                    patchMapObjectsDataFieldValues({ mapObjects: [{ id: node.id, dataFieldValues: { [mapObjectNameDataField.id]: name }, layerId }], fulfilledNotification: true })
                );
                treeBrowserContext.updateNodeLocally(updatedNode);
                break;
            }
            default:
                throw new Error(`Node type of ${node.type} is not supported to change name`);
        }
        setEditName(false);
        dispatch(sidebar.actions.setEditingItem(false));
    };

    const lockDataTestId = `lock-${node.name}-node-button`;

    const showLockedState = canEdit(mapAuthorisation) && (isSelected || onMouseOver || isNode("locked"));

    const interactableNodeTypes: NodeType[] = [NodeType.Layer, NodeType.Folder];

    return (
        <StyledSidebarNode
            data-testid={dataTestIds.sidebar.sidebarNode(node.name)}
            data-cy="sidebar-node"
            data-is-grouped={isInGroup}
            nodeHeight={nodeHeight}
            indentLevel={indent}
            type={node.type}
            hidden={!isNode("visible")}
            selected={isSelected}
            showRighthandButtons={node.id === popoverNodeId}
            isDragging={isDragging}
            expanded={canExpand}
            // Mouse leave event triggers on popover being rendered, cursor hasn't left if popover is for that layer
            onMouseLeave={() => setMouseOver(popoverNodeId === node.id)}
            onMouseEnter={() => setMouseOver(true)}
            className={`sidebar-node-${node.type}`}
            locked={isNode("locked")}
        >
            {disableOverlay()}
            {/* Used to check if layer is selected in e2e tests */}
            <div data-testid={isSelected && dataTestIds.sidebar.nodeSelected(node.name)} />
            {/* Hidden elements */}
            <InteractiveElement
                onClick={(e) => {
                    if (!selectedNodeIds.includes(node.id)) {
                        selectNode(e);
                    }
                }}
                className="hover-indicator"
                data-testid={`select-layer-${node.name?.replaceAll(" ", "")}`}
            >
                <span className="hidden">Click to select: {node.name}</span>
            </InteractiveElement>
            <div className="indent" />
            {/* Hidden elements */}
            {/* Visible elements */}
            {canExpand && (
                <InteractiveElement
                    data-testid={dataTestIds.sidebar.expandNode(node.name)}
                    onClick={(e) => {
                        e.stopPropagation();
                        treeBrowserContext.toggleExpandNode(
                            node,
                            mapLayers.find((l) => l.id === node.id)?.viewBy !== ViewByStatus.OBJECT,
                            mapLayers.find((l) => l.id === node.id)?.viewBy === ViewByStatus.OBJECT
                        );
                    }}
                    className="node-segment icon caret"
                >
                    <span className="hidden">
                        {expanded ? <span>{`Collapse ${node.type}`}</span> : canExpand ? <span>{`Expand ${node.type}`}</span> : <span>No interaction available</span>}
                    </span>
                    {canExpand && <FontAwesomeIcon icon={isLoading ? "circle-notch" : expanded ? "caret-down" : "caret-right"} spin={isLoading} />}
                </InteractiveElement>
            )}
            {children}
            {/* Node Name Display */}
            <div className="node-segment">
                <SidebarItemComponent
                    onClick={(_, event) => selectNode(event)}
                    id={node.id}
                    dataCy={`node-name-${node.name}`}
                    name={node.name}
                    selected={isSelected}
                    singleSelection={isOnlyChildInSelection(tree, selectedNodeIds, [], node.id)}
                    editName={editName}
                    className="name"
                    changeEditName={() => {
                        setEditName(true);
                        dispatch(sidebar.actions.setEditingItem(true));
                    }}
                    onItemNameChange={handleNameChange}
                    itemHeight={`${nodeHeight}px`}
                    allowEditName={node?.type === NodeType.MapObject ? (mapLayer?.locked ? false : canEdit(node.authorisation)) : isAdmin(node.authorisation)}
                    visible={isNode("visible")}
                    onInvalidNameInputted={() => toast.info({ title: translate(Content.common.alert), message: translate(Content.map.sidebar.node_name) })}
                />
                {additionalElements}
            </div>
            {/* INTERACTIVE ICONS */}
            {!isDragging && permissableAuthorisations.includes(node.authorisation) && interactableNodeTypes.includes(node.type) && (
                <>
                    {!isAllChildrenEmptyFolders && (
                        <>
                            {/* Lock icon */}
                            {showLockedState && (
                                <LockIconComponent
                                    locked={isNode("locked")}
                                    isEditingNode={isEditingNode}
                                    onMouseOver={onMouseOver}
                                    onClick={(e) => toggleAttribute(e, "locked")}
                                    dataTestId={lockDataTestId}
                                />
                            )}
                            {/* Visibility icon */}
                            <EyeIconComponent
                                visible={isNode("visible")}
                                isEditing={isEditingNode}
                                onMouseOver={onMouseOver}
                                onClick={(e) => toggleAttribute(e, "visible")}
                                testId={dataTestIds.sidebar.layerVisibilityIcon(node.name)}
                                editingTooltip={
                                    node.type === NodeType.Layer ? Content.map5.verticalBarErrors.hide_layer_composition : Content.map5.verticalBarErrors.group_composition
                                }
                                hideTooltip={node.type === NodeType.Layer ? Content.map3.layerOptionsTooltips.hide_layer : Content.map5.mapObjectSidebarOptions.hide_group}
                                showTooltip={node.type === NodeType.Layer ? Content.map3.layerOptionsTooltips.show_layer : Content.map5.mapObjectSidebarOptions.show_group}
                            />
                        </>
                    )}
                    <ClickAwayListenerToolTip onClickAway={() => setLayerInfo(false)}>
                        <StyledInfoTooltip
                            title={translate(Content.map4.layer_information.title)}
                            text={
                                <IventisErrorBoundaryWithToast>
                                    <LayerInformation
                                        node={node}
                                        openPermissionsModal={() => {
                                            setPermissionModalOpen(true);
                                            setLayerInfo(false);
                                        }}
                                    />
                                </IventisErrorBoundaryWithToast>
                            }
                            open={layerInfo}
                            onClose={() => setLayerInfo(false)}
                            placement="right-start"
                        >
                            <InteractiveElement
                                className="node-segment icon layer-overflow"
                                data-testid="sidebar-context-menu-button"
                                data-cy={`sidebar-context-menu-button-${node.name}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOverflowOpen(!overflowOpen);

                                    // Make sure the layer information panel closes incase it is open, the click away listener won't activate here
                                    setLayerInfo(false);

                                    if (!isSelected) {
                                        selectNode(e);
                                    }
                                }}
                                ref={(ref) => {
                                    if (ref != null) {
                                        overflowButtonRef.current = ref;
                                    }
                                }}
                            >
                                <span className="hidden">{translate(Content.map5.layer_overflow.hidden_text)}</span>
                                <FontAwesomeIcon icon="ellipsis-vertical" />
                            </InteractiveElement>
                        </StyledInfoTooltip>
                    </ClickAwayListenerToolTip>
                    <PermissionsEditModalComponent
                        open={permissionModalOpen}
                        node={node}
                        close={() => {
                            setPermissionModalOpen(false);
                        }}
                    />
                </>
            )}
            {/* Visible elements */}
            <ClickAwayListener onClickAway={() => setOverflowOpen(false)}>
                <div>
                    {overflowOpen && selectedNodeIds.includes(node.id) && (
                        <SidebarContextMenu
                            anchorElement={overflowButtonRef.current}
                            node={node}
                            actions={{
                                showLayerInfo: () => setLayerInfo(true),
                                selectNode: (e) => selectNode(e),
                                showDeleteConfirmation: () => setDeleteConfirmationModalOpen(true),
                                showLayerProperties: () => setPermissionModalOpen(true),
                                copyLayer: () => setLayerActionModal("copy"),
                                close: () => setOverflowOpen(false),
                                exportLayers: handleExportLayers,
                                importGeojson: () => setLayerActionModal("importGeoJson"),
                                addAttributes: () => setLayerActionModal("addAttributes"),
                                moveLayerToBackOfMap: () => {
                                    const filteredLayers = mapLayers.filter((layer) => layer.remote);
                                    const updatedOrderValue = getNextLowestLayerMapOrderValue(filteredLayers);
                                    dispatch(patchMapLayers([{ id: node.id, mapOrder: updatedOrderValue }]));
                                    incrementMonitoringCount({ name: MetricName.MOVE_LAYER_TO_BACK, resourceId: node.id });
                                },
                                moveLayerToFrontOfMap: () => {
                                    const filteredLayers = mapLayers.filter((layer) => layer.remote);
                                    const updatedOrderValue = getNextHighestLayerMapOrderValue(filteredLayers);
                                    dispatch(patchMapLayers([{ id: node.id, mapOrder: updatedOrderValue }]));
                                    incrementMonitoringCount({ name: MetricName.MOVE_LAYER_TO_FRONT, resourceId: node.id });
                                },
                                toggleAllLayersVisibility: () => {
                                    if (isAdmin(mapAuthorisation)) {
                                        setToggleVisibilityConfirmationOpen(true);
                                    } else {
                                        handleToggleAllLayersVisibility();
                                    }
                                    setOverflowOpen(false);
                                },
                                upgradePlan,
                            }}
                            open={overflowOpen}
                        />
                    )}
                </div>
            </ClickAwayListener>
            {deleteConfirmationModalOpen && (
                <DeleteNodeModal
                    onConfirm={(selectedNodes) => {
                        handleDeleteNodes(selectedNodes);
                        // Close the overflow menu
                        setOverflowOpen(false);
                    }}
                    onClose={() => {
                        setDeleteConfirmationModalOpen(false);
                    }}
                />
            )}
            <ConfirmationDialogComponent
                show={toggleVisibilityConfirmationOpen}
                handleConfirm={() => {
                    setToggleVisibilityConfirmationOpen(false);
                    handleToggleAllLayersVisibility();
                }}
                handleCancel={() => setToggleVisibilityConfirmationOpen(false)}
                title={translate(Content.map3.toggleAllLayersVisibilityConfirmation.title)}
                message={translate(Content.map3.toggleAllLayersVisibilityConfirmation.message)}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.cancel)}
            />
        </StyledSidebarNode>
    );
};

export const getAllSitemapLevels = (sitemaps: Sitemap[]): { index: number; name: string; abbreviation: string }[] => {
    const levels = [];
    sitemaps.forEach((sitemap) => {
        if (sitemap != null) {
            sitemap.versions.forEach((version) => {
                version.sitemapVersionLevels.forEach((level) => {
                    const existingLevel = levels.find((l) => l.index === level.levelIndex);
                    if (existingLevel == null) {
                        // Create a new level for that index
                        levels.push({
                            index: level.levelIndex,
                            abbreviation: level.levelAbbreviation,
                            name: level.levelName,
                        });
                    } else {
                        // Append names to existing level if they differ
                        if (!existingLevel.abbreviation.includes(level.levelAbbreviation)) {
                            existingLevel.abbreviation = `${existingLevel.abbreviation} / ${level.levelAbbreviation}`;
                        }
                        if (!existingLevel.name.includes(level.levelName)) {
                            existingLevel.name = `${existingLevel.name} / ${level.levelName}`;
                        }
                    }
                });
            });
        }
    });
    return levels;
};

const LayerNodeComponent: NodeComponent<Node, SidebarTreeBrowserExtraProps> = ({ node, extraProps, getInteractivity }) => {
    const [layer, setLayer] = useState<DomainLayer>(null);
    const { setSidebarPopoverAnchor, handleDeleteNodes } = extraProps as SidebarTreeBrowserExtraProps;

    const history = useHistory();

    const { mapLayers, mapLayer, layerIsEditing, mapName, user } = useSelector((state: RootState) => ({
        mapLayers: state.mapReducer.mapModule.layers.value,
        mapLayer: state.mapReducer.mapModule.layers.value.find((lyr) => lyr.id === node.id) as DomainLayer,
        layerIsEditing: state.sidebarReducer.modalLayer?.id === node.id && state.sidebarReducer.modalLayer?.anchor === "node",
        mapName: state.mapReducer.mapName,
        user: state.auth.user,
    }));

    const translate = useIventisTranslate();

    useEffect(() => {
        if (mapLayers != null && mapLayers.length !== 0) {
            const stateLayer: DomainLayer = mapLayers.find((mapLayer) => mapLayer.id === node.id);
            if (stateLayer != null) {
                setLayer(stateLayer);
            }
        }
    }, [mapLayers]);

    const { userPreferences } = useSelector(({ auth: { userPreferences } }: RootState) => ({
        userPreferences,
    }));

    const [{ tree, expandedNodeIds, loadingNodeIds, mainNodeId, selectedNodeIds }] = useTreeBrowser(
        TreeBrowserDomainNodeContext,
        node.id,
        "tree",
        "expandedNodeIds",
        "loadingNodeIds",
        "mainNodeId",
        "selectedNodeIds"
    );
    const isGrouped = findNode([tree], node.parentId)?.type === NodeType.Folder;
    const dispatch: RootDispatch = useDispatch();

    const { draggable }: ReturnType<typeof getInteractivity> = useMemo(
        () =>
            getInteractivity == null ? { disabled: false, selectable: { disabled: false, tooltip: undefined }, draggable: true, isNameClickable: true } : getInteractivity(node),
        [node, getInteractivity]
    );

    const { setRefs: setNodeRef, attributes, listeners, isOver } = useDragAndDropSortable(node.id, draggable);

    const expanded = expandedNodeIds.includes(node.id);

    const loading = useMemo(() => loadingNodeIds.includes(node.id), [loadingNodeIds, node.id]);

    const handleNavigateToProjectSettings = () => {
        history.push("/project-settings/project-attributes");
    };

    const isUserProjectAdmin = useMemo(() => user.projectAdmin || user.globalAuthorisation === Authorisation.Admin, [user]);

    const mainNode = useMemo(() => findNode([tree], mainNodeId), [tree, mainNodeId]);
    const indent = getNodeNestedLevel(mainNode, node);

    const isSelected = selectedNodeIds.includes(node.id);

    if (!mapLayer) {
        return <></>;
    }

    return (
        <div ref={setNodeRef} {...attributes} {...listeners}>
            {isSelected && <SelectedNodeIndicator data-testid={`layer-selected-inidcator-${node.name}`} />}
            <SidebarTreeNodeComponent
                setSidebarPopoverAnchor={setSidebarPopoverAnchor}
                node={node as Node}
                indent={indent}
                expanded={expanded}
                canExpand
                mapLayer={mapLayer}
                isLoading={loading}
                isDragging={extraProps.isDragging}
                handleDeleteNodes={handleDeleteNodes}
                setLayerActionModal={extraProps.setModal}
                isInGroup={isGrouped}
            >
                {isOver && <DragAndDropItemPlacement insideGroup={isGrouped} dataTestId={`node-drop-target-${sanitiseForDOMToken(node.name)}`} />}
                <LayerPreviewComponent
                    id={layer?.id}
                    name={layer?.name}
                    imported={layer?.imported}
                    onClick={() => {
                        dispatch(sidebar.actions.setModalLayer({ id: layer.id, anchor: "node" }));
                    }}
                    mapLayer={mapLayer}
                />
            </SidebarTreeNodeComponent>
            {expanded && !loading && node.childNodes.length === 0 && (
                <NewStyledEmptyNode marginLeft={indent + MARGIN_WIDTH_PX}>
                    <StyledEmptyNodeMessage>{translate(Content.map4.sidebarLayers.emptyLayer, { layerName: node.name })}</StyledEmptyNodeMessage>
                </NewStyledEmptyNode>
            )}
            <LayerEditModal
                layer={mapLayer}
                open={layerIsEditing}
                mapName={mapName}
                close={() => {
                    dispatch(sidebar.actions.setModalLayer(null));
                }}
                showTour={userPreferences.showLayerTour}
                finishTour={() => dispatch(updateUserPreferences({ showLayerTour: false }))}
                navigateToProjectSettings={isUserProjectAdmin ? handleNavigateToProjectSettings : null}
                canUserEdit={isAdmin(node.authorisation)}
                skipTemplateSelection={false}
                showMapObjectDataFieldSelectionForTooltip
            />
        </div>
    );
};

const ObjectNodeComponent: NodeComponent<Node, SidebarTreeBrowserExtraProps> = ({ node, extraProps }) => {
    const [{ tree, mainNodeId }] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "tree", "mainNodeId");

    const { setSidebarPopoverAnchor } = extraProps;

    const { parentNode } = useMemo(() => getParentNode(tree, node), [tree, node]);

    const { mapLayer, mapLayers, objectGeometry, sitemaps, level, sitemapConfigs } = useSelector((state: RootState) => {
        const localToRemoteMapObjectIdsMap = state.mapReducer.mapModule.localToRemoteMapObjectIdsMap.value;
        return {
            mapLayer: state.mapReducer.mapModule.layers.value.find((lyr) => lyr.id === node.parentId) as DomainLayer,
            mapLayers: state.mapReducer.mapModule.layers.value,
            objectGeometry: state.mapReducer.mapModule.geoJSON.value[parentNode?.id]?.find((obj) => localToRemoteMapObjectIdsMap[obj.objectId] === node.id) as LocalGeoJsonObject,
            sitemaps: state.mapReducer.sitemaps,
            level: state.mapReducer.mapModule.currentLevel,
            sitemapConfigs: state.mapReducer.sitemapConfigs,
        };
    });

    const layer = useMemo(() => getParentLayer(mapLayers, parentNode), [parentNode, mapLayers]);

    const mainNode = useMemo(() => findNode([tree], mainNodeId), [tree, mainNodeId]);
    const indent = getNodeNestedLevel(mainNode, node);

    if (parentNode == null || layer == null) {
        return null;
    }

    const levelName = getMapObjectLevelName(sitemaps, objectGeometry?.feature?.properties?.level, objectGeometry?.feature?.properties?.sitemapId, sitemapConfigs);

    const mapObjectLevel = objectGeometry?.feature?.properties?.level;

    const additionalElements = (
        <>
            {levelName !== undefined && (
                <StyledLevelMessage data-testid={`level-name-sidebar-${levelName}`} $isOnCurrentLevel={level === mapObjectLevel}>
                    ({levelName})
                </StyledLevelMessage>
            )}
            {objectGeometry?.feature?.properties?.modeOfTransport != null && <StyledRouteIcon data-testid="route-object-icon" icon={["far", "route"]} />}
        </>
    );

    return (
        <SidebarTreeNodeComponent
            setSidebarPopoverAnchor={setSidebarPopoverAnchor}
            handleDeleteNodes={extraProps.handleDeleteNodes}
            indent={indent}
            node={node}
            mapLayer={mapLayer}
            setLayerActionModal={extraProps.setModal}
            additionalElements={additionalElements}
        >
            <ObjectPreviewComponent id={parentNode.id} name={parentNode.name} imported={false} layerStyle={layer ? getLayerStyle(layer) : null} object={objectGeometry?.feature} />
        </SidebarTreeNodeComponent>
    );
};

const StyledRouteIcon = styled(FontAwesomeIcon)`
    width: 44px;
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
    font-size: ${fontSizes.small};
`;
const StyledLevelMessage = styled.span<{ $isOnCurrentLevel: boolean }>`
    font-size: ${fontSizes.xSmall};
    padding-right: 12px;
    color: ${({ theme, $isOnCurrentLevel }: { theme: Theme; $isOnCurrentLevel: boolean }) =>
        $isOnCurrentLevel ? theme.typographyColors.lessSubdued : theme.typographyColors.evenMoreSubdued};
    text-transform: lowercase;
    &::first-letter {
        text-transform: capitalize;
    }
`;

const DataFieldListItemNodeComponent: NodeComponent<Node, SidebarTreeBrowserExtraProps> = ({ node, extraProps }) => {
    const [{ tree, mainNodeId }] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "tree", "mainNodeId");

    const { setSidebarPopoverAnchor } = extraProps;

    const { parentNode } = useMemo(() => getParentNode(tree, node), [tree, node]);

    const { mapLayers } = useSelector((state: RootState) => ({
        mapLayers: state.mapReducer.mapModule.layers.value,
    }));

    const layer = useMemo(() => getParentLayer(mapLayers, parentNode), [parentNode, mapLayers]);

    const mainNode = useMemo(() => findNode([tree], mainNodeId), [tree, mainNodeId]);
    const indent = getNodeNestedLevel(mainNode, node);

    if (parentNode == null) {
        return null;
    }

    return (
        <SidebarTreeNodeComponent
            setSidebarPopoverAnchor={setSidebarPopoverAnchor}
            handleDeleteNodes={extraProps.handleDeleteNodes}
            indent={indent}
            node={node}
            setLayerActionModal={extraProps.setModal}
        >
            {/* For models and line models just use the same thumbnail as the layer */}
            <DataFieldPreviewComponent id={parentNode.id} name={parentNode.name} imported={false} layerStyle={layer ? getLayerStyle(layer) : null} listItemId={node.id} />
        </SidebarTreeNodeComponent>
    );
};

const FolderNodeComponent: NodeComponent<Node, SidebarTreeBrowserExtraProps> = ({ node, extraProps, getInteractivity }) => {
    const translate = useIventisTranslate();

    const { setSidebarPopoverAnchor } = extraProps;

    const [{ tree, expandedNodeIds, mainNodeId, nodeIdsWithChildrenSelected, selectedNodeIds }] = useTreeBrowser(
        TreeBrowserDomainNodeContext,
        node.id,
        "tree",
        "expandedNodeIds",
        "mainNodeId",
        "nodeIdsWithChildrenSelected",
        "selectedNodeIds"
    );

    const { draggable }: ReturnType<typeof getInteractivity> = useMemo(
        () =>
            getInteractivity == null ? { disabled: false, selectable: { disabled: false, tooltip: undefined }, draggable: true, isNameClickable: true } : getInteractivity(node),
        [node, getInteractivity]
    );

    const { setRefs: setNodeRef, attributes, listeners, isOver, isDragging } = useDragAndDropSortable(node.id, draggable);

    const mainNode = useMemo(() => findNode([tree], mainNodeId), [tree, mainNodeId]);
    const indent = getNodeNestedLevel(mainNode, node);

    const isGrouped = findNode([tree], node.parentId)?.type === NodeType.Folder;

    const expanded = expandedNodeIds.includes(node.id);

    const isChildSelected = nodeIdsWithChildrenSelected.includes(node.id);
    const isSelected = selectedNodeIds.includes(node.id);

    return (
        <div ref={setNodeRef} {...attributes} {...listeners}>
            {(isChildSelected || isSelected) && <SelectedNodeIndicator data-testid={`child-within-group-selected-inidcator-${node.name}`} />}
            <SidebarTreeNodeComponent
                canExpand
                expanded={expanded}
                setSidebarPopoverAnchor={setSidebarPopoverAnchor}
                handleDeleteNodes={extraProps.handleDeleteNodes}
                indent={indent}
                node={node as Node}
                isDragging={isDragging}
                setLayerActionModal={extraProps.setModal}
                isInGroup={isGrouped}
            >
                {isOver && <DragAndDropItemPlacement dataTestId={`node-drop-target-${sanitiseForDOMToken(node.name)}`} insideGroup={false} />}
                <InteractiveElement className="node-segment icon preview-icon">
                    <FontAwesomeIcon className="icon" icon={["far", "folder"]} />
                </InteractiveElement>
            </SidebarTreeNodeComponent>
            {expanded && node.childNodes.length === 0 && (
                <NewStyledEmptyNode marginLeft={indent + MARGIN_WIDTH_PX}>
                    <StyledEmptyNodeMessage data-testid={`empty-group-message-${node.name}`}>
                        {translate(Content.map4.sidebarLayers.emptyGroup, { groupName: node.name })}
                    </StyledEmptyNodeMessage>
                </NewStyledEmptyNode>
            )}
        </div>
    );
};

interface NodePreviewProps {
    id: string;
    name: string;
    imported?: boolean;
    onClick?: () => void;
    mapLayer?: DomainLayer;
}

interface DataFieldPreviewProps extends NodePreviewProps {
    layerStyle: LayerStyle;
    listItemId: string;
}

interface ObjectPreviewProps extends NodePreviewProps {
    layerStyle: LayerStyle;
    object: LocalGeoJsonObject["feature"];
}

interface LayerTypeIconProps extends NodePreviewProps {
    layerStyle: LayerStyle;
    styleSelector: MapObjectProperties | string;
}

export const LAYER_THUMBNAIL_KEY = "layer-thumbnail";

/**
 * The most basic of previews.
 * @returns a NodePreviewComponent using the snapshot taken from the layer preview that is saved in S3 as the thumbnail.
 */
const LayerPreviewComponent: FunctionComponent<NodePreviewProps> = ({ name, imported, onClick, mapLayer }) => {
    const { data: layerThumbnailAssetUrl } = useQuery(
        [LAYER_THUMBNAIL_KEY, mapLayer.id],
        async () => {
            const res = await getLayerThumbnailAssets([mapLayer]);
            return res?.length ? res[0]?.assetUrl : null;
        },
        { retry: false, refetchOnMount: false }
    );

    const theme = useTheme<Theme>();
    return (
        <NodePreviewComponent
            testId={name}
            onClick={onClick}
            imported={imported}
            thumbnail={
                <div className="preview-background">
                    {layerThumbnailAssetUrl ? <img style={{ height: "100%" }} src={layerThumbnailAssetUrl} alt={name} /> : sidebarNodeIcons(theme)[NodeType.Layer]}
                </div>
            }
        />
    );
};

/**
 * This component will return the matching svg icon required based on the layer type passed. It will also use the styleSelector to extract datadriven styles either by using map
 * object properties or using a listItemId.
 * @returns a NodePreviewComponent with the correct SVG icon for the thumbnail.
 */
const LayerTypeIcon: FunctionComponent<LayerTypeIconProps> = ({ onClick, imported, layerStyle, styleSelector }) => {
    const thumbnail = LayerIconGenerated({
        style: layerStyle,
        styleSelector,
        previewIconUrlGetter: (id: string) => assetUrlGetter(id),
        getAsset: assetService.getAsset,
        getModelThumbnail: async (modelId: string) => {
            const [model] = await editStyleService.modelGetter([modelId]);
            return assetUrlGetter(model.thumbnailAssetId);
        },
    });
    return <NodePreviewComponent onClick={onClick} imported={imported} thumbnail={thumbnail} />;
};

/**
 * A node preview component for data field nodes
 * @returns a NodePreviewComponent using the listItemId & layer style to produce a data driven preview.
 */
const DataFieldPreviewComponent: FunctionComponent<DataFieldPreviewProps> = ({ id, name, imported, onClick, layerStyle, listItemId }) => {
    if (layerStyle && listItemId != null) {
        return <LayerTypeIcon id={id} name={name} imported={imported} onClick={onClick} layerStyle={layerStyle} styleSelector={listItemId} />;
    }
    return (
        <NodePreviewComponent
            onClick={onClick}
            imported={imported}
            thumbnail={
                <StyledPreviewContainer className="preview-background">
                    <LoadingComponent size={20} />
                </StyledPreviewContainer>
            }
        />
    );
};

/**
 * A node preview component for object nodes
 * @returns a NodePreviewComponent using the the object properties & layer style to produce a data driven preview.
 */
const ObjectPreviewComponent: FunctionComponent<ObjectPreviewProps> = ({ id, name, imported, onClick, layerStyle, object }) => {
    if (layerStyle && object != null) {
        return <LayerTypeIcon id={id} name={name} imported={imported} onClick={onClick} layerStyle={layerStyle} styleSelector={object?.properties} />;
    }
    return (
        <NodePreviewComponent
            onClick={onClick}
            imported={imported}
            thumbnail={
                <StyledPreviewContainer className="preview-background">
                    <ObjectPreviewSkeleton />
                </StyledPreviewContainer>
            }
        />
    );
};

const ObjectPreviewSkeleton = styled.div`
    width: 100%;
    height: 100%;
    opacity: 0.5;
    ${skeletonShimmer({ sequence: 0 })};
`;

/**
 * The bare bones node preview. This contains the interactive element, thumbnail and imported icon.
 * @returns an InteractiveElement
 */
const NodePreviewComponent: FunctionComponent<{ onClick?: () => void; imported: boolean; thumbnail: ReactNode; testId?: string }> = ({ onClick, imported, thumbnail, testId }) => (
    <InteractiveElement
        data-testid={dataTestIds.sidebar.layerPreview(testId)}
        className="node-segment icon preview-icon"
        onClick={(e) => {
            e.stopPropagation();
            onClick?.();
        }}
    >
        {thumbnail}
        {imported && (
            <div data-testid={`layer-preview-${testId}-link-icon`} className="icon link-icon">
                <FontAwesomeIcon size="xs" icon="link" />
            </div>
        )}
    </InteractiveElement>
);

export const sidebarNodeComponents = {
    [NodeType.Layer]: LayerNodeComponent,
    [NodeType.MapObject]: ObjectNodeComponent,
    [NodeType.DataFieldListItem]: DataFieldListItemNodeComponent,
    [NodeType.Folder]: FolderNodeComponent,
};

export const sidebarNodeIcons = (theme: Theme) => ({
    [NodeType.Layer]: <FontAwesomeIcon icon={["far", "layer-group"]} style={{ color: theme.shades.two }} />,
});

const StyledPreviewContainer = styled.div`
    svg {
        width: auto;
        height: auto;
    }

    .icon-image {
        height: 100%;
    }

    .default-icon {
        ${({ theme }: { theme: Theme }) => theme.shades.two};
        width: 75%;
    }
`;

const StyledInfoTooltip = styled(InfoTooltip)`
    & .MuiTooltip-tooltip {
        background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
        color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued70};
        box-shadow: 0 0 5px 1px ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        max-width: 800px;
    }
    & .MuiTooltip-arrow {
        color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
        &:before {
            box-shadow: 0 0 5px 1px ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        }
    }

    button {
        color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued70};
    }
`;
